import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, ArrowRight, Loader2 } from "lucide-react";
import { API_BASE_URL } from "@/config/api";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "0x4AAAAAAEhHvMqPIXh2gCOF";
const TURNSTILE_SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let turnstileScriptPromise;

const sha256Hex = async (value) => {
  const bytes = new TextEncoder().encode(value);
  const hash = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(hash), (byte) => byte.toString(16).padStart(2, "0")).join("");
};

const loadTurnstileScript = () => {
  if (window.turnstile) {
    return Promise.resolve(window.turnstile);
  }
  if (turnstileScriptPromise) {
    return turnstileScriptPromise;
  }

  turnstileScriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.turnstile);
    script.onerror = () => reject(new Error("Unable to load Turnstile."));
    document.head.appendChild(script);
  });

  return turnstileScriptPromise;
};

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [captchaReady, setCaptchaReady] = useState(!TURNSTILE_SITE_KEY);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const captchaEnabled = Boolean(TURNSTILE_SITE_KEY);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!captchaEnabled) {
      return undefined;
    }

    let mounted = true;

    loadTurnstileScript()
      .then((turnstile) => {
        if (!mounted || !turnstileRef.current || !turnstile) {
          return;
        }

        // Render explicitly so React controls the widget lifecycle.
        widgetIdRef.current = turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action: "contact",
          theme: "light",
          callback: (token) => {
            setCaptchaToken(token);
            setCaptchaReady(true);
            setCaptchaError("");
          },
          "expired-callback": () => {
            setCaptchaToken("");
            setCaptchaReady(false);
          },
          "error-callback": () => {
            setCaptchaToken("");
            setCaptchaReady(false);
            setCaptchaError("Security check failed to load. Please refresh and try again.");
          },
        });
      })
      .catch(() => {
        if (mounted) {
          setCaptchaReady(false);
          setCaptchaError("Security check failed to load. Please refresh and try again.");
        }
      });

    return () => {
      mounted = false;
      if (widgetIdRef.current !== null && window.turnstile?.remove) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [captchaEnabled]);

  const resetCaptcha = () => {
    if (captchaEnabled && widgetIdRef.current !== null && window.turnstile?.reset) {
      window.turnstile.reset(widgetIdRef.current);
      setCaptchaToken("");
      setCaptchaReady(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    if (captchaEnabled && !captchaToken) {
      toast.error("Please complete the security check.");
      return;
    }
    setLoading(true);
    try {
      const submission = captchaEnabled ? { ...form, "cf-turnstile-response": captchaToken } : form;
      const payload = JSON.stringify(submission);
      const contentSha256 = await sha256Hex(payload);
      await axios.post(`${API_BASE_URL}/contact`, payload, {
        headers: {
          "Content-Type": "application/json",
          "x-amz-content-sha256": contentSha256,
        },
      });
      toast.success("Thanks — we'll be in touch within one business day.");
      setSent(true);
      setForm({ name: "", email: "", company: "", message: "" });
      resetCaptcha();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Submission failed. Please try again.");
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 bg-zinc-950 text-white overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.12] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.12) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.12) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#7C9BFF]">
            Get in touch
          </span>
          <h2 className="mt-4 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1.02]">
            Govern AI.
            <br /> Modernize your enterprise today.
          </h2>
          <p className="mt-6 text-zinc-300 leading-relaxed">
            Schedule a custom demo of AI Inventory & Compliance Governance — or talk to our team
            about your cloud migration and modernization roadmap.
          </p>

          <ul className="mt-10 space-y-4 text-sm">
            <li className="flex items-center gap-3 text-zinc-200">
              <span className="inline-flex items-center justify-center h-9 w-9 border border-zinc-700">
                <Mail size={15} />
              </span>
              <a href="mailto:jaisphere@gmail.com" data-testid="contact-email" className="hover:text-white">
                jaisphere@gmail.com
              </a>
            </li>
            <li className="flex items-center gap-3 text-zinc-200">
              <span className="inline-flex items-center justify-center h-9 w-9 border border-zinc-700">
                <MapPin size={15} />
              </span>
              Atlanta, Georgia · United States
            </li>
          </ul>
        </div>

        <form
          onSubmit={submit}
          data-testid="contact-form"
          className="col-span-12 lg:col-span-7 bg-white text-zinc-950 p-8 md:p-10 rounded-sm border border-zinc-800"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Name" id="name" required>
              <input
                id="name"
                data-testid="contact-input-name"
                value={form.name}
                onChange={update("name")}
                className="input-style"
                placeholder="Jane Cooper"
              />
            </Field>
            <Field label="Work email" id="email" required>
              <input
                id="email"
                type="email"
                data-testid="contact-input-email"
                value={form.email}
                onChange={update("email")}
                className="input-style"
                placeholder="jane@company.com"
              />
            </Field>
            <Field label="Company" id="company" className="sm:col-span-2">
              <input
                id="company"
                data-testid="contact-input-company"
                value={form.company}
                onChange={update("company")}
                className="input-style"
                placeholder="Acme Corp"
              />
            </Field>
            <Field label="What can we help you with?" id="message" required className="sm:col-span-2">
              <textarea
                id="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={update("message")}
                rows={5}
                className="input-style resize-none"
                placeholder="Tell us about your AI governance or cloud goals…"
              />
            </Field>
          </div>

          {captchaEnabled && (
            <div className="mt-6 min-h-[74px]">
              <div ref={turnstileRef} data-testid="contact-turnstile" />
              {captchaError && (
                <p className="mt-2 text-xs text-red-600" role="alert">
                  {captchaError}
                </p>
              )}
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-4">
            <p className="text-xs text-zinc-500">
              By submitting, you agree to our privacy policy.
            </p>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading || (captchaEnabled && !captchaReady)}
              className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-60 text-white px-6 py-3 rounded-sm text-sm font-semibold transition-colors"
            >
              {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
              {sent ? "Sent" : loading ? "Sending…" : "Request demo"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .input-style {
          width: 100%;
          background: #fff;
          border: 1px solid #e4e4e7;
          padding: 12px 14px;
          font-size: 0.95rem;
          border-radius: 2px;
          outline: none;
          transition: border-color .2s, box-shadow .2s;
          color: #09090b;
        }
        .input-style:focus {
          border-color: #002FA7;
          box-shadow: 0 0 0 3px rgba(0,47,167,0.15);
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, id, required, children, className = "" }) => (
  <label htmlFor={id} className={`block ${className}`}>
    <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
      {label}
      {required && <span className="text-[#002FA7]"> *</span>}
    </span>
    <div className="mt-2">{children}</div>
  </label>
);

export default Contact;
